import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { ContentControlledEditingDirectiveViewModel } from 'app/js/src/domain/content-control/editing-directive';
import {
  ContentControlledEventActionEnum,
  ContentControlledMediaTypeEnum,
} from 'app/js/src/domain/content-control/enums';
import { MatSelect, MatSelectChange } from "@angular/material/select";
import { FocusOptions } from "@angular/cdk/a11y";
import { ContentControlledKeyframe } from 'app/js/src/domain/content-control/keyframe';
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators";
import { MediaRepository } from "app/js/src/domain/media-repository";
import { ETimelineMode } from 'app/js/src/domain/content-control/studio/e-timeline-mode';

type videoEffect = {
  displayName: string;
  type: ContentControlledEventActionEnum;
};

export interface VideoClip {
  id: string | number;
  name: string;
  duration: string;
  durationMs: number;
  thumbnailUrl: string;
}

@Component({
  selector: "evrideo-content-control-studio-actions",
  templateUrl: "./content-control-studio-actions.component.html",
  styleUrl: "./content-control-studio-actions.component.scss",
})
export class ContentControlStudioActionsComponent implements OnInit, OnDestroy {
  @ViewChild("hideObject") hideObjectRef: ElementRef<HTMLDivElement>;
  @ViewChild("hideAudio") hideAudioRef: ElementRef<HTMLDivElement>;
  @ViewChild("effectsBody") effectsBodyTemplate!: TemplateRef<any>;
  @ViewChild("videoClipsBody") videoClipsBodyTemplate!: TemplateRef<any>;
  @Input("is-subtitles-available") isSubtitlesAvailable: boolean = false;
  @Input("is-live") isLive: boolean = false;
  @Input("selected-segment")
  selectedSegment: ContentControlledEditingDirectiveViewModel;
  @Input("timeline-mode") timelineMode: ETimelineMode = ETimelineMode.OriginalTime;
  @Output("on-button-clicked") onButtonClicked = new EventEmitter<number>();
  @Output("on-selected-segment-changes") onSelectedSegmentChanges =
    new EventEmitter<ContentControlledEditingDirectiveViewModel>();
  @Output("on-video-clip-add") onVideoClipAdd = new EventEmitter<VideoClip>();
  public selectedTab = "effects";
  public selectedTabBody: TemplateRef<any>;
  public videoClipsSearchQuery = "";
  public filteredVideoClips: VideoClip[] = [];
  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription;

  public disableEverything = false;
  public allowCut = true;
  public videoEffects: Array<Array<videoEffect>> = [
    [
      {
        displayName: "Black rectangle",
        type: ContentControlledEventActionEnum.BlackRect,
      },
      {
        displayName: "Blur rectangle",
        type: ContentControlledEventActionEnum.BlurRect,
      },
      {
        displayName: "Pixlate rectangle",
        type: ContentControlledEventActionEnum.PixelateRect,
      },
    ],
    [
      {
        displayName: "Black oval",
        type: ContentControlledEventActionEnum.BlackOval,
      },
      {
        displayName: "Blur oval",
        type: ContentControlledEventActionEnum.BlurOval,
      },
      {
        displayName: "Pixlate oval",
        type: ContentControlledEventActionEnum.PixelateOval,
      },
    ],
    [
      {
        displayName: "Black screen",
        type: ContentControlledEventActionEnum.BlackScreen,
      },
      {
        displayName: "Blur screen",
        type: ContentControlledEventActionEnum.BlurScreen,
      },
      {
        displayName: "Pixlate screen",
        type: ContentControlledEventActionEnum.PixelateScreen,
      },
    ],
  ];

  public selectedVideoEffect;
  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private mediaRepository: MediaRepository,
  ) {}

  ngOnInit() {
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((q) =>
          this.mediaRepository.getMedia(
            q, 0, 0, "Name", null, null, null, null, "All", [], [], [], [],
          )
        ),
      )
      .subscribe((items) => {
        this.filteredVideoClips = items.map((item) => ({
          id: item.id,
          name: item.name ?? item.userFilename,
          duration: item.contentDuration ? item.formatDuration() : "",
          durationMs: item.contentMediaDurationMs ?? 0,
          thumbnailUrl: item.contentSnapshotUrl,
        }));
      });
    this.searchSubject.next("");
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  onSearchQueryChange(q: string) {
    this.videoClipsSearchQuery = q;
    this.searchSubject.next(q);
  }

  ngAfterViewInit() {
    this.selectedTabBody = this.effectsBodyTemplate;
    this.changeDetectorRef.detectChanges();
  }

  buttonClicked(action: ContentControlledEventActionEnum) {
    if (this.disableEverything) {
      return;
    }
    if (this.selectedSegment.action != ContentControlledEventActionEnum.Empty) {
      return;
    }
    if (
      action === ContentControlledEventActionEnum.Cut &&
      this.timelineMode === ETimelineMode.EditedTime
    ) {
      return;
    }
    if (action === ContentControlledEventActionEnum.Cut) {
      this.disableEverything = true;
    }
    this.onButtonClicked.emit(action);
  }

  public get CCAction(): typeof ContentControlledEventActionEnum {
    return ContentControlledEventActionEnum;
  }

  resetEverything() {
    this.HideObjectMenu();
    this.HideAudioMenu();
  }
  setDisabledOptions() {
    this.disableEverything =
      !this.selectedSegment ||
      this.selectedSegment.action === ContentControlledEventActionEnum.Cut;
    this.allowCut =
      this.selectedSegment &&
      this.selectedSegment.action == ContentControlledEventActionEnum.Empty &&
      this.timelineMode === ETimelineMode.OriginalTime;
  }
  toggleShowHideObjectMenu() {
    if (
      this.selectedSegment.action == ContentControlledEventActionEnum.Empty ||
      this.selectedSegment.mediaType == ContentControlledMediaTypeEnum.Video
    ) {
      this.hideObjectRef?.nativeElement.classList.toggle("reveal");
      this.HideAudioMenu();
    }

    return true;
  }
  toggleShowHideAudioMenu() {
    if (
      this.selectedSegment.action == ContentControlledEventActionEnum.Empty ||
      this.selectedSegment.mediaType == ContentControlledMediaTypeEnum.Audio
    ) {
      this.hideAudioRef?.nativeElement.classList.toggle("reveal");
      this.HideObjectMenu();
    }
    return true;
  }
  HideObjectMenu() {
    this.hideObjectRef?.nativeElement.classList.remove("reveal");
    return true;
  }
  HideAudioMenu() {
    this.hideAudioRef?.nativeElement.classList.remove("reveal");
    return true;
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedSegment) {
      try {
        if (!changes.selectedSegment.currentValue) {
          this.resetEverything();
        } else {
          const ss = changes.selectedSegment
            .currentValue as ContentControlledEditingDirectiveViewModel;
          if (ss.mediaType === ContentControlledMediaTypeEnum.Video) {
            this.videoEffects.flatMap((x) => x);

            this.selectedVideoEffect = this.videoEffects
              .flat()
              .find((x) => x.type === ss.action);
            if (
              ss.action != ContentControlledEventActionEnum.Empty &&
              !this.hideObjectRef.nativeElement.classList.contains("reveal")
            ) {
              this.toggleShowHideObjectMenu();
            }
          } else if (ss.mediaType === ContentControlledMediaTypeEnum.Audio) {
            if (
              ss.action === ContentControlledEventActionEnum.Beep &&
              !this.hideObjectRef?.nativeElement.classList.contains("reveal")
            ) {
              this.toggleShowHideAudioMenu();
            }
          } else {
            this.selectedVideoEffect = { displayName: "", type: 0 };
            this.resetEverything();
          }
        }
      } finally {
        this.setDisabledOptions();
      }
    }
  }
  isVideoScreenEffect(effect?: videoEffect) {
    effect = effect ?? this.selectedVideoEffect;
    if (!effect) return false;
    return this.videoEffects[2].map((x) => x.type).includes(effect.type);
  }
  isVideoRectangleEffect(effect: videoEffect) {
    return (
      effect && this.videoEffects[0].map((x) => x.type).includes(effect.type)
    );
  }
  isVideoCircleEffect(effect: videoEffect) {
    return (
      effect && this.videoEffects[0].map((x) => x.type).includes(effect.type)
    );
  }

  onVideoEffectChange(e: MatSelectChange) {
    if (
      this.selectedSegment.action != ContentControlledEventActionEnum.Empty &&
      this.selectedSegment.mediaType != ContentControlledMediaTypeEnum.Video
    ) {
      return;
    }
    this.selectedVideoEffect = e.value;
    e.source.close();

    this.onButtonClicked.emit(e.value.type);
  }

  onDensityChange(v: number) {
    (this.selectedSegment.instructions ??= {}).density = v;
    this.onSelectedSegmentChanges.emit(this.selectedSegment);
  }
  onSampleDistanceChange(v: number) {
    (this.selectedSegment.instructions ??= {}).sampleDistance = v;
    this.onSelectedSegmentChanges.emit(this.selectedSegment);
  }
  onBeepFrequencyChange(v: number) {
    (this.selectedSegment.instructions ??= {}).beepFrequency = v;
    this.onSelectedSegmentChanges.emit(this.selectedSegment);
  }
  onScalingFactorChange(v: number) {
    (this.selectedSegment.instructions ??= {}).scaleFactor = v;
    this.onSelectedSegmentChanges.emit(this.selectedSegment);
  }

  formatBeepFrequencyLabel(value: number): string {
    return `${value}Hz`;
  }
  selectTab(tab: string) {
    this.selectedTab = tab;
    if (tab === "videoClips") {
      this.selectedTabBody = this.videoClipsBodyTemplate;
    } else {
      this.selectedTabBody = this.effectsBodyTemplate;
    }
  }
}
